import { useEffect, useState, startTransition } from "react";
import { api } from "@/lib/api";
import { setAmenityTranslations } from "@/lib/amenityI18n";

export const AMENITY_CATALOG = {
  Allgemein: ["Bettwäsche & Handtücher inklusive", "Kinderfreundlich", "Ruhige Lage", "Haustiere erlaubt", "Langzeitmiete möglich", "Strandurlaub", "Golfurlaub", "Rollstuhlgeeignet", "Barrierefrei", "Mietwagen empfohlen"],
  Außen: ["Pool", "Kinderpool", "Whirlpool", "Sauna", "Außendusche", "Meerblick", "Bergblick", "Poolblick", "Gartenblick", "Wasserblick", "Terrasse", "Dachterrasse", "Balkon", "Wintergarten", "Garten", "Gartenmöbel", "Sonnenliegen", "Liegewiese", "Sonnenschirm", "Markise", "Grill", "Kinderspielplatz", "Sportgeräte", "Strandkorb", "Strandhandtücher", "Parkplatz", "Aufzug", "Waschmaschine", "Gemeinschafts-Waschmaschine", "Wäschetrockner", "Bügelbrett & Bügeleisen", "Safe"],
  Wohnen: ["WLAN", "TV (deutsche Sender)", "DVD-Player", "Musikanlage", "Radio", "Telefon", "PC", "Schreibtisch", "Schlafsofa", "Esstisch", "Bücher", "Spiele", "Heizung", "Klimaanlage", "Kamin"],
  Küche: ["Kühlschrank", "Gefrierfach", "Herd (2 Platten)", "Herd (3 Platten)", "Herd (4 Platten)", "Backofen", "Mikrowelle", "Geschirrspüler", "Kaffeemaschine", "Wasserkocher", "Toaster", "Saftpresse", "Mixer", "Eierkocher", "Kinderhochstuhl", "Esstisch in der Küche", "Brötchenservice"],
  Schlafen: ["Doppelbett", "Zwei Einzelbetten", "Einzelbett", "Stockbett", "Kinderbett", "Schlafsofa im Schlafzimmer", "Heizung im Schlafzimmer", "Klimaanlage im Schlafzimmer", "TV im Schlafzimmer"],
  Bad: ["Dusche", "Massagedusche", "Badewanne", "Whirlpool-Badewanne", "Bidet", "Föhn", "Handtücher", "Bademäntel", "Heizung im Bad"],
};
export const AMENITY_GROUPS = Object.keys(AMENITY_CATALOG);

const base = (a) => a.replace(/\s*\([^)]*cm\)\s*$/i, "");

let catalog = null;
let loading = null;
const listeners = new Set();

const applyCatalog = (data) => {
  const byGroup = {};
  data.items.forEach((a) => (byGroup[a.group] ||= []).push(a));
  catalog = { ...data, byGroup, groupKeys: data.groups.map((g) => g.key).filter((g) => byGroup[g]?.length) };
  const tr = {};
  data.items.forEach((a) => { tr[a.label.de] = [a.label.en, a.label.es, a.label.nl, a.label.pl, a.label.fr, a.label.it]; });
  setAmenityTranslations(tr);
  startTransition(() => listeners.forEach((fn) => fn(catalog)));
};

export const loadAmenityCatalog = () => {
  if (catalog) return Promise.resolve(catalog);
  if (!loading) loading = api.get("/amenities").then((r) => { applyCatalog(r.data); return catalog; }).catch(() => { loading = null; return null; });
  return loading;
};
export const refreshAmenityCatalog = () => { catalog = null; loading = null; return loadAmenityCatalog(); };

export const useAmenityCatalog = () => {
  const [c, setC] = useState(catalog);
  useEffect(() => {
    if (!catalog) loadAmenityCatalog().then((c) => startTransition(() => setC(c)));
    listeners.add(setC);
    return () => listeners.delete(setC);
  }, []);
  return c;
};

export const groupAmenities = (list) => {
  const grouped = {};
  const rest = [];
  const cat = catalog?.byGroup;
  const groups = catalog?.groupKeys || AMENITY_GROUPS;
  list.forEach((a) => {
    const g = groups.find((k) => (cat ? cat[k]?.some((x) => x.label.de === base(a)) : AMENITY_CATALOG[k].includes(base(a))));
    if (g && g !== "Weitere") (grouped[g] ||= []).push(a); else rest.push(a);
  });
  if (rest.length) grouped.Weitere = rest;
  return grouped;
};

export const QUICK_FILTERS = [
  { key: "tv", label: { de: "Deutsches TV", en: "German TV", es: "TV alemana" } },
  { key: "pool", label: { de: "Pool", en: "Pool", es: "Piscina" } },
  { key: "wifi", label: { de: "Internetzugang", en: "Internet access", es: "Acceso a internet" } },
];
export const MORE_FILTERS = [
  { key: "sea_view", label: { de: "Meerblick", en: "Sea view", es: "Vista al mar" } },
  { key: "pets", label: { de: "Haustiere erlaubt", en: "Pets allowed", es: "Mascotas" } },
  { key: "aircon", label: { de: "Klimaanlage", en: "Air conditioning", es: "Aire acondicionado" } },
  { key: "heating", label: { de: "Heizung", en: "Heating", es: "Calefacción" } },
  { key: "washer", label: { de: "Waschmaschine", en: "Washing machine", es: "Lavadora" } },
  { key: "dishwasher", label: { de: "Geschirrspüler", en: "Dishwasher", es: "Lavavajillas" } },
  { key: "parking", label: { de: "Parkplatz", en: "Parking", es: "Aparcamiento" } },
  { key: "terrace", label: { de: "Terrasse / Balkon", en: "Terrace / balcony", es: "Terraza / balcón" } },
  { key: "garden", label: { de: "Garten", en: "Garden", es: "Jardín" } },
  { key: "bbq", label: { de: "Grill", en: "Barbecue", es: "Barbacoa" } },
  { key: "whirlpool", label: { de: "Whirlpool", en: "Hot tub", es: "Jacuzzi" } },
  { key: "sauna", label: { de: "Sauna", en: "Sauna", es: "Sauna" } },
  { key: "elevator", label: { de: "Aufzug", en: "Elevator", es: "Ascensor" } },
  { key: "kids", label: { de: "Kinderfreundlich", en: "Child-friendly", es: "Apto para niños" } },
  { key: "accessible", label: { de: "Barrierefrei", en: "Accessible", es: "Accesible" } },
  { key: "longterm", label: { de: "Langzeitmiete", en: "Long-term rental", es: "Alquiler larga estancia" } },
  { key: "quiet", label: { de: "Ruhige Lage", en: "Quiet location", es: "Zona tranquila" } },
];

export const ALL_FILTERS = [...QUICK_FILTERS, ...MORE_FILTERS];
